import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState
} from 'react';
import { v4 as uuid } from 'uuid';
import { tryParse } from '@pasqal/core/ui/components/pulser-studio/util/parsing';
import {
  formatSequenceError,
  type PulserSequence
} from '~/models/pulser-schema';
import * as studioModel from '~/models/studio';
import { parseUploadedExperiment } from '~/models/uploadedExperiment';
import {
  clamp,
  createLocalStorageSynchronized,
  findClosestValidAtomPosition,
  keyboardConsumed,
  labelGenerator
} from '~/util';
import { useWindowEvent } from '~/util/hooks';
import { type Transaction, UndoableState } from '~/util/undoableState';
import { exportToPulser } from './export';
import { importFromPulser } from './import';
import { computeLayout, type PulseLayout } from './layout';
import { experimentReducer } from './reducer';
import type { ReactNode } from 'react';
import type { Atom, ExperimentCommand } from '~/models/studio';
import type { Vec2 } from '~/util';

export type ReplaceListener = () => void;

export interface ComputedPulse {
  readonly pulse: studioModel.Pulse;
  readonly layout: PulseLayout;
}

// Base interface that the experiment system returns
export interface BaseExperimentState {
  clear: () => void;
  getBlockadeRadius: (amplitude: number) => number;
  getC6Coefficient: () => number;
  openTransaction: (label: string) => Transaction<ExperimentCommand>;
  createNewPulse: (channelUuid: string) => string | undefined;
  createAtom: () => void;
  addManyAtoms: (positions: Vec2[], labelPrefix?: string) => Set<string>;
  exportPulser: () => ReturnType<typeof exportToPulser>;
  undoStack: {
    canUndo: () => boolean;
    canRedo: () => boolean;
    undo: () => void;
    redo: () => void;
  };
  onChange: (cb: ReplaceListener) => () => void;
  onReplace: (cb: ReplaceListener) => () => void;
  load: (exp: string, raw?: boolean) => string | null;
  loadSaved: () => void;
  experiments: studioModel.StudioExperiment[];
  addExperiment: () => void;
  closeExperiment: (index: number) => void;
  setCurrentExperiment: (index: number) => void;
  currentExperimentIndex: number;
  isSyncingState: boolean;
  state: UndoableState<
    Readonly<{
      name: string;
      device: studioModel.Device;
      register: Atom[];
      channels: studioModel.Channel[];
      pulses: studioModel.Pulse[];
      basis: studioModel.Basis | null;
      metadata?: PulserSequence['metadata'];
    }>,
    studioModel.ExperimentCommand
  >;
}

// Enhanced interface with reactive state (provided by ExperimentProvider)
export interface ExperimentState extends BaseExperimentState {
  // Reactive state (automatically updated)
  temporaryState: studioModel.StudioExperiment | null;
  commitedState: studioModel.StudioExperiment | null;
  name: string;
  atoms: readonly studioModel.Atom[];
  commitedAtoms: readonly studioModel.Atom[];
  commitedBasis: studioModel.Basis | null;
  device: studioModel.Device;
  commitedDevice: studioModel.Device;
  channels: readonly studioModel.Channel[];
  commitedChannels: readonly studioModel.Channel[];
  pulses: readonly studioModel.Pulse[];
  commitedPulses: readonly studioModel.Pulse[];
  computedPulses: readonly ComputedPulse[];
  computedCommittedPulses: readonly ComputedPulse[];
  maxTime: number;

  // From BaseExperimentState
  experiments: studioModel.StudioExperiment[];
  currentExperimentIndex: number;
}

// c6 coefficients for rydberg levels 50-100
const c6 = [
  96120.72, 122241.6, 154693.02, 194740.36, 243973.91, 304495.01, 378305.98,
  468027.05, 576714.85, 707911.38, 865723.02, 1054903.11, 1281042.11,
  1550531.15, 1870621.31, 2249728.57, 2697498.69, 3224987.51, 3844734.37,
  4571053.32, 5420158.53, 6410399.4, 7562637.31, 8900342.14, 10449989.62,
  12241414.53, 14308028.03, 16687329.94, 19421333.62, 22557029.94, 26146720.74,
  30248886.65, 34928448.69, 40257623.67, 46316557.88, 53194043.52, 60988354.64,
  69808179.15, 79773468.88, 91016513.07, 103677784.57, 117933293.96,
  133943541.9, 151907135.94, 172036137.34, 194562889.89, 219741590.56,
  247850178.91, 279192193.77, 314098829.39, 352931119.11
];

const atomLabelGenerator = (
  existingAtoms: readonly Atom[],
  prefix: string
): (() => string) => {
  const existing = existingAtoms
    .filter((a) => a.label.startsWith(prefix))
    .map((a) => a.label);
  return labelGenerator(existing, prefix);
};

const createExperimentSystem = (): BaseExperimentState => {
  const emptyExperiment = studioModel.defaultData();

  const persistent = createLocalStorageSynchronized('experiments', {
    experiments: [
      {
        experimentData: JSON.stringify(emptyExperiment),
        undoStack: '[]',
        redoStack: '[]'
      }
    ],
    _persistenceTimestamp: 0
  });

  const state = new UndoableState<
    studioModel.StudioExperiment,
    ExperimentCommand
  >(emptyExperiment, experimentReducer);

  let isSyncingState = false;
  let currentExperimentIndex = 0;
  let experiments: studioModel.StudioExperiment[] = [emptyExperiment];
  let syncTimeout: ReturnType<typeof setTimeout> | undefined = undefined;
  let syncTimestamp = 0;
  const replaceListeners = new Set<ReplaceListener>();
  const changeListeners = new Set<ReplaceListener>();

  function notifyReplace() {
    replaceListeners.forEach((cb) => cb());
    notifyChange();
  }

  function notifyChange() {
    changeListeners.forEach((cb) => cb());
  }

  // Re-render on committed state changes (commit, undo, redo, replace)
  state.subscribe(notifyChange);

  // Persist to localStorage on every committed state change, debounced
  state.subscribe(() => {
    if (isSyncingState) {
      return;
    }
    if (syncTimeout !== undefined) {
      clearTimeout(syncTimeout);
    }
    syncTimeout = setTimeout(
      () => serializeExperiment(currentExperimentIndex),
      SYNC_INTERVAL
    );
    // Sync name from committed state back into the experiments array so the tab bar updates
    const committed = state.getCommitedState();
    if (experiments[currentExperimentIndex]?.name !== committed.name) {
      experiments = experiments.map((exp, i) =>
        i === currentExperimentIndex ? { ...exp, name: committed.name } : exp
      );
    }
  });

  // Re-render on temporary state changes (drags), throttled to one per animation frame
  let rafPending = false;
  state.subscribeTemp(() => {
    if (!rafPending) {
      rafPending = true;
      requestAnimationFrame(() => {
        rafPending = false;
        notifyChange();
      });
    }
  });

  function onReplace(cb: ReplaceListener): () => void {
    replaceListeners.add(cb);
    return () => replaceListeners.delete(cb);
  }

  function onChange(cb: ReplaceListener): () => void {
    changeListeners.add(cb);
    return () => changeListeners.delete(cb);
  }

  function validIndex(index: number) {
    return index >= 0 && index < experiments.length;
  }

  function deserializeExperiment(index: number) {
    const { experiments: persistedExps } = persistent.get();
    const currentExp = persistedExps?.[index] ?? null;
    if (!currentExp) {
      return { experiment: emptyExperiment, undoStack: [], redoStack: [] };
    }
    const experimentData = tryParse(currentExp.experimentData).andThen(
      studioModel.parseStudioExperiment
    );
    const undoStack = tryParse(currentExp.undoStack).andThen(
      studioModel.parseExperimentCommandStack
    );
    const redoStack = tryParse(currentExp.redoStack).andThen(
      studioModel.parseExperimentCommandStack
    );
    if (experimentData.ok && undoStack.ok && redoStack.ok) {
      return {
        experiment: experimentData.val,
        undoStack: undoStack.val,
        redoStack: redoStack.val
      };
    } else {
      return { experiment: emptyExperiment, undoStack: [], redoStack: [] };
    }
  }

  const SYNC_INTERVAL = 500;

  function serializeExperiment(index: number) {
    if (index < 0 || index >= persistent.get().experiments.length) {
      return;
    }
    syncTimeout = undefined;
    const pTimestamp = persistent.get()._persistenceTimestamp;
    syncTimestamp = Date.now();
    if (pTimestamp < syncTimestamp) {
      const p = persistent.get();
      p.experiments[index] = {
        experimentData: JSON.stringify(state.getCommitedState()),
        undoStack: JSON.stringify(state.undoStack),
        redoStack: JSON.stringify(state.redoStack)
      };
      p._persistenceTimestamp = Date.now();
      persistent.set({ ...p });
    }
  }

  // Write any debounced change now, before tab operations read or reshuffle storage,
  // otherwise it gets lost or saved under the wrong tab index
  function flushPendingSync() {
    if (syncTimeout !== undefined) {
      clearTimeout(syncTimeout);
      serializeExperiment(currentExperimentIndex);
    }
  }

  function setCurrentExperiment(index: number) {
    if (index < 0) {
      return;
    }
    flushPendingSync();
    const length = persistent.get().experiments?.length ?? 0;
    const safeIndex = length > 0 ? Math.min(index, length - 1) : 0;
    isSyncingState = true;
    const currentExp = deserializeExperiment(safeIndex);
    state.replace(
      currentExp.experiment,
      currentExp.undoStack,
      currentExp.redoStack
    );
    currentExperimentIndex = index;
    notifyReplace();
    isSyncingState = false;
  }

  function addExperiment() {
    flushPendingSync();
    isSyncingState = true;
    experiments = [...experiments, emptyExperiment];
    const p = persistent.get();
    p.experiments.push({
      experimentData: JSON.stringify(emptyExperiment),
      undoStack: '[]',
      redoStack: '[]'
    });
    p._persistenceTimestamp = Date.now();
    persistent.set({ ...p });
    isSyncingState = false;
    setCurrentExperiment(p.experiments.length - 1);
  }

  function closeExperiment(index: number) {
    flushPendingSync();
    isSyncingState = true;
    experiments = experiments.filter((_, i) => i !== index);
    const p = persistent.get();
    p.experiments.splice(index, 1);
    p._persistenceTimestamp = Date.now();
    persistent.set({ ...p });
    if (p.experiments.length > 0) {
      setCurrentExperiment(Math.min(index, p.experiments.length - 1));
    } else {
      currentExperimentIndex = -1;
      isSyncingState = false;
      notifyReplace();
    }
  }

  function loadSaved() {
    const p = persistent.get();
    if (p.experiments) {
      reloadAllStored(currentExperimentIndex);
    }
  }

  function reloadAllStored(currentIndex: number) {
    const p = persistent.get();
    if (p._persistenceTimestamp > syncTimestamp) {
      syncTimestamp = p._persistenceTimestamp;
      isSyncingState = true;
      const storedExperiments = p.experiments.map((_xp, index) =>
        deserializeExperiment(index)
      );
      experiments = storedExperiments.map(({ experiment }) => experiment);
      let idx = currentIndex;
      if (!validIndex(idx)) {
        idx = experiments.length - 1;
      }
      const cur = storedExperiments[idx];
      if (cur) {
        state.replace(cur.experiment, cur.undoStack, cur.redoStack);
        currentExperimentIndex = idx;
        notifyReplace();
      }
      isSyncingState = false;
    }
  }

  function load(exp: string, raw = true): string | null {
    try {
      const parsed = raw
        ? tryParse(exp).andThen(parseUploadedExperiment)
        : parseUploadedExperiment(exp as unknown as object);

      if (parsed.ok) {
        const data = importFromPulser(parsed.val);
        state.replace(data);
        notifyReplace();
        return null;
      }
      return formatSequenceError(parsed.val);
    } catch (e) {
      return e instanceof Error ? e.message : 'Invalid sequence';
    }
  }

  function clear() {
    state.replace(emptyExperiment);
    notifyReplace();
  }

  function getDevice(): studioModel.Device {
    return state.getTemporaryState().device as studioModel.Device;
  }

  function getAtoms(): readonly studioModel.Atom[] {
    return state.getTemporaryState().register;
  }

  function getBlockadeRadius(amplitude: number): number {
    const rydbergLevel = getDevice().rydbergLevel;
    return Math.pow(c6[rydbergLevel - 50] / amplitude, 1 / 6);
  }

  function getC6Coefficient() {
    return c6[getDevice().rydbergLevel - 50];
  }

  function openTransaction(_label: string): Transaction<ExperimentCommand> {
    // TODO: Add the devtools code from the Vue version here
    return state.openTransaction();
  }

  function createNewPulse(channelUuid: string): string | undefined {
    const st = state.getCommitedState();
    const channel = st.channels.find((c) => c.uuid === channelUuid);
    if (channel == null) {
      return;
    }
    const isLocal = channel.addressing === 'local';
    const { maxAmp, maxAbsDetuning } = channel;

    const atom = st.register[0];
    if (isLocal && atom == null) {
      return;
    }
    const targetUuid = isLocal ? atom.uuid : null;

    const tx = openTransaction('createNewPulse');
    const pulseUuid = uuid();
    tx.push({
      kind: 'add_pulse',
      pulse: {
        uuid: pulseUuid,
        channelUuid: channel.uuid,
        targetUuid,
        protocol: 'min-delay',
        dependencies: [],
        delay: 0,
        amplitude: [
          {
            kind: 'blackman',
            constraint: {
              kind: 'area',
              maxVal: Math.min(maxAmp, Math.PI * 4),
              duration: clamp(
                250,
                channel.minDuration,
                channel.maxDuration ?? 10000
              )
            }
          }
        ],
        detuning: !channel.enableDetuning
          ? null
          : [
              {
                kind: 'ramp',
                duration: clamp(
                  250,
                  channel.minDuration,
                  channel.maxDuration ?? 10000
                ),
                start: 0,
                stop: Math.min(maxAbsDetuning, Math.PI * 2)
              }
            ],
        phase: 0,
        postPhaseShift: 0
      },
      insertBefore: null
    });
    tx.commit();
    return pulseUuid;
  }

  function addManyAtoms(positions: Vec2[], labelPrefix = 'q'): Set<string> {
    const tx = openTransaction('addManyAtoms');
    const nameGen = atomLabelGenerator(getAtoms(), labelPrefix);
    const addedUuids = new Set<string>();
    for (let i = 0; i < positions.length; i++) {
      const pos = positions[i];
      const newUuid = uuid();
      addedUuids.add(newUuid);
      tx.push({
        kind: 'add_atom',
        atom: { label: nameGen(), pos, uuid: newUuid },
        insertBefore: null
      });
    }
    tx.commit();
    return addedUuids;
  }

  function createAtom() {
    const labelPrefix = 'q';
    const st = state.getCommitedState();
    const device = st.device;
    if (st.register.length === device.maxAtomNum) {
      return;
    }

    const idealPos = { x: 0, y: 0 };
    const newPos = findClosestValidAtomPosition(
      idealPos,
      st.register.map((atom) => atom.pos),
      device.minAtomDistance,
      device.maxRadialDistance
    );
    const label = atomLabelGenerator(getAtoms(), labelPrefix)();

    const tx = openTransaction('createAtom');
    tx.push({
      kind: 'add_atom',
      atom: { label, pos: newPos, uuid: uuid() },
      insertBefore: null
    });
    tx.commit();
  }

  function exportPulser() {
    return exportToPulser(state.getCommitedState());
  }

  const undoStack = {
    canUndo: () => state.canUndo(),
    canRedo: () => state.canRedo(),
    undo: () => state.undo(),
    redo: () => state.redo()
  };

  // Set up beforeunload to persist
  window.addEventListener('beforeunload', () => {
    if (syncTimeout !== undefined) {
      clearTimeout(syncTimeout);
      serializeExperiment(currentExperimentIndex);
    }
  });

  // Subscribe to cross-tab storage changes
  persistent.subscribe(() => {
    reloadAllStored(currentExperimentIndex);
  });

  return {
    clear,
    getBlockadeRadius,
    getC6Coefficient,
    openTransaction,
    createNewPulse,
    createAtom,
    addManyAtoms,
    exportPulser,
    undoStack,
    onChange,
    onReplace,
    load,
    loadSaved,
    get experiments() {
      return experiments;
    },
    addExperiment,
    closeExperiment,
    setCurrentExperiment,
    get currentExperimentIndex() {
      return currentExperimentIndex;
    },
    get isSyncingState() {
      return isSyncingState;
    },
    // Expose the internal state for reactive hooks
    state
  };
};

const useKeyboardShortcuts = (undo: () => void, redo: () => void) => {
  useWindowEvent(
    'keydown',
    useCallback(
      (e) => {
        if (keyboardConsumed()) {
          return;
        }
        switch (e.key) {
          case 'z':
          case 'Z':
            if (e.ctrlKey || e.metaKey) {
              if (e.shiftKey) {
                redo();
              } else {
                undo();
              }
            }
            break;
        }
      },
      [undo, redo]
    )
  );
};

const ExperimentContext = createContext<ExperimentState | null>(null);
ExperimentContext.displayName = 'Experiment';

export const ExperimentProvider = ({ children }: { children: ReactNode }) => {
  const [experimentSystem] = useState<BaseExperimentState>(() =>
    createExperimentSystem()
  );

  // Direct access to internal state for proper reactivity
  const internalState = experimentSystem.state; // UndoableState instance

  // Subscribe to both committed and temporary state changes
  const [temporaryState, setTemporaryState] = useState(() =>
    internalState.getTemporaryState()
  );
  const [commitedState, setCommitedState] = useState(() =>
    internalState.getCommitedState()
  );

  // Subscribe to experiment list changes
  const [experiments, setExperiments] = useState(
    () => experimentSystem.experiments
  );
  const [currentExperimentIndex, setCurrentExperimentIndex] = useState(
    () => experimentSystem.currentExperimentIndex
  );

  useEffect(() => {
    if (!internalState) {
      return;
    }

    return experimentSystem.onChange(() => {
      setCommitedState(internalState.getCommitedState());
      setTemporaryState(internalState.getTemporaryState());
      setExperiments([...experimentSystem.experiments]);
      setCurrentExperimentIndex(experimentSystem.currentExperimentIndex);
    });
  }, [internalState, experimentSystem]);

  // Set up keyboard shortcuts
  useKeyboardShortcuts(
    experimentSystem.undoStack.undo,
    experimentSystem.undoStack.redo
  );

  const temporaryLayouts = useMemo(
    () => computeLayout(temporaryState),
    [temporaryState]
  );

  const computedPulses: ComputedPulse[] = useMemo(() => {
    if (!temporaryState) {
      return [];
    }
    return temporaryState.pulses.map((pulse) => ({
      pulse: pulse as studioModel.Pulse,
      layout: temporaryLayouts[pulse.uuid]
    }));
  }, [temporaryLayouts, temporaryState]);

  const computedCommittedPulses: ComputedPulse[] = useMemo(() => {
    const layouts = computeLayout(commitedState);
    return commitedState.pulses.map((pulse) => ({
      pulse: pulse as studioModel.Pulse,
      layout: layouts[pulse.uuid]
    }));
  }, [commitedState]);

  const maxTime = useMemo(() => {
    return Object.values(temporaryLayouts).reduce(
      (max, p) => Math.max(max, p.time.max),
      0
    );
  }, [temporaryLayouts]);

  // Create reactive context value - automatically updates when state changes
  const contextValue = useMemo(
    (): ExperimentState => ({
      ...experimentSystem,
      // Reactive state (directly from UndoableState)
      temporaryState,
      commitedState,
      // Convenience accessors (automatically reactive)
      name: commitedState.name || '',
      atoms: temporaryState.register || [],
      commitedAtoms: commitedState.register || [],
      commitedBasis: commitedState.basis,
      channels: temporaryState.channels || [],
      commitedChannels: commitedState.channels || [],
      device: temporaryState.device,
      commitedDevice: commitedState.device,
      pulses: temporaryState.pulses || [],
      commitedPulses: commitedState?.pulses || [],
      computedPulses,
      computedCommittedPulses,
      maxTime,
      experiments: experiments || [],
      currentExperimentIndex: currentExperimentIndex || 0
    }),
    [
      experimentSystem,
      temporaryState,
      commitedState,
      computedPulses,
      computedCommittedPulses,
      maxTime,
      experiments,
      currentExperimentIndex
    ]
  );
  return (
    <ExperimentContext.Provider value={contextValue}>
      {children}
    </ExperimentContext.Provider>
  );
};

export const useExperiment = (): ExperimentState => {
  const ctx = useContext(ExperimentContext);
  if (!ctx) {
    throw new Error('Experiment not provided');
  }
  return ctx;
};
